// Processes and services an agent started, stopped or interrogated on the host.
//
//   taskkill //IM agentic-e2e-orchestrator.exe //F
//   systemctl restart forgejo-runner
//
// The target is the PROCESS NAME, not the pid: a pid is meaningful for minutes and
// to one machine, while `agentic-e2e-orchestrator` is a thing the reader knows and
// the same name recurs every time it is restarted. A pid is kept as evidence.
//
// The reading is whether the agent CONFIRMED the outcome. Killing a process is a
// change to the machine's state, and these tools say plainly whether it worked —
// `SUCCESS: The process … has been terminated` versus a silent failure.

import { flagOf, positionalsOf, shellStatements } from "../../src/mine/statements.js";

const TOOLS = /^(?:systemctl|launchctl|sc|service|pkill|kill|killall|taskkill|supervisorctl|net)$/i;
const STOP = /^(?:stop|kill|terminate|disable|unload)$/i;
const START = /^(?:start|restart|reload|enable|load)$/i;

/** The process or service being addressed, with Windows' flag forms handled. */
export function nameOf(st) {
  const win = /\/\/?IM\s+([\w.-]+)/i.exec(st.text);
  if (win) return win[1].replace(/\.exe$/i, "");
  const bare = positionalsOf(st.args, new Set(["-s", "--signal", "-u", "--user"]));
  const named = bare.find((t) => /^[A-Za-z][\w.@-]*$/.test(t) && !STOP.test(t) && !START.test(t)
    && !/^(?:status|show|list|is-active|is-enabled|cat|daemon-reload)$/i.test(t));
  if (!named) return null;
  const clean = named.replace(/\.(?:exe|service)$/i, "");
  // A bare English word is not a process name. `net` and `taskkill` take free-form
  // arguments and a loose positional read pulled the word "identical" out of one,
  // rendering a group for a process that does not exist. Unit managers are exempt:
  // there a bare word genuinely is the unit.
  const managerNamesUnits = /^(?:systemctl|launchctl|service|supervisorctl)$/i.test(st.program);
  if (!managerNamesUnits && !/[-_.\d]/.test(clean)) return null;
  return clean;
}

export function pidOf(st) {
  const win = /\/\/?PID\s+(\d+)/i.exec(st.text);
  if (win) return win[1];
  const bare = positionalsOf(st.args, new Set(["-s", "--signal"]));
  return bare.find((t) => /^\d{2,7}$/.test(t)) || null;
}

const CONFIRMED_RE = /\bSUCCESS:|has been terminated|\bActive:\s+active|\bstarted\b|\bstopped\b/i;
const FAILED_RE = /\bERROR:|not found|no process found|could not be terminated|Access is denied|Failed to (?:stop|start|restart)|Unit .* not loaded/i;

export function extractProcsOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    if (!TOOLS.test(st.program)) continue;
    const bare = positionalsOf(st.args, new Set(["-s", "--signal", "-u", "--user"]));
    const verb = bare.find((t) => STOP.test(t) || START.test(t))
      || (/^(?:pkill|killall|taskkill|kill)$/i.test(st.program) ? "kill" : bare[0] || "status");
    const kind = STOP.test(verb) || verb === "kill" ? "stop" : START.test(verb) ? "start" : "inspect";
    const name = nameOf(st);
    const pid = pidOf(st);
    if (!name && !pid) continue;

    const out = st.output || "";
    ops.push({
      key: st.key,
      process: name || `pid ${pid}`,
      // A pid is evidence, never identity: it means something for minutes, and only
      // on one machine.
      pid,
      verb: String(verb).toLowerCase(),
      kind,
      manager: st.program,
      outcome: !out ? "unrecorded" : FAILED_RE.test(out) ? "failed" : CONFIRMED_RE.test(out) ? "confirmed" : "unconfirmed",
      output: st.output,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind, cmd: st.text.replace(/\s+/g, " ").slice(0, 200) }],
      checks: [{ kind, tool: name || `pid ${pid}`, scope: [st.program, pid && name ? `pid ${pid}` : null].filter(Boolean),
        outcome: !out ? "unrecorded" : FAILED_RE.test(out) ? "failed" : CONFIRMED_RE.test(out) ? "confirmed" : "unconfirmed" }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (op.outcome === "failed") return "the manager reported failure — the process was not in the state the agent asked for";
  if (op.outcome === "unconfirmed") return "the command returned, but nothing in the output confirms the process actually changed state";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — the manager's answer never entered this trace";
    case "none": return "ran and captured no output";
    default: return null;
  }
}
